<script setup>
import { ref, watch } from 'vue'
import { Calendar } from 'lucide-vue-next'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import DateTimePicker from '@/components/DateTimePicker.vue'
import { useMeetingStore } from '@/store/meetingStore'

const props = defineProps({
  client: {
    type: String,
    default: '',
  },
})

const meetingStore = useMeetingStore()

// Meeting form data
const newMeeting = ref({
  patientId: props.client,
  title: 'Consultation',
  description: '',
  startTime: new Date(
    Math.ceil(Date.now() / (30 * 60 * 1000)) * (30 * 60 * 1000) + 60 * 60 * 1000,
  ),
  duration: 30,
  ownerName: '',
  ownerEmail: '',
})

// Set client ID when prop changes
watch(() => props.client, (newClientId) => {
  if (newClientId) {
    newMeeting.value.patientId = newClientId
  }
})

// Error handling
const schedulingError = ref('')

// Create a meeting
async function createMeeting() {
  schedulingError.value = ''

  if (!newMeeting.value.patientId) {
    schedulingError.value = 'Veuillez sélectionner un patient'
    return
  }

  if (!newMeeting.value.title) {
    schedulingError.value = 'Veuillez ajouter un titre'
    return
  }

  // Use the store to schedule the meeting
  const result = await meetingStore.scheduleMeeting(newMeeting.value)

  if (result.success) {
    meetingStore.showNewMeetingModal = false
  }
  else {
    schedulingError.value = result.error
  }
}

// Reset form when modal is opened
watch(() => meetingStore.showNewMeetingModal, (isOpen) => {
  if (isOpen) {
    newMeeting.value = {
      patientId: props.client || (meetingStore.patients.length > 0 ? meetingStore.patients[0].id : ''),
      title: 'Consultation',
      description: '',
      startTime: new Date(
        Math.ceil(Date.now() / (30 * 60 * 1000)) * (30 * 60 * 1000) + 60 * 60 * 1000,
      ),
      duration: 30,
      ownerName: meetingStore.currentUser?.name || '',
      ownerEmail: meetingStore.currentUser?.email || '',
    }
    schedulingError.value = ''
  }
})
</script>

<template>
  <Dialog :open="meetingStore.showNewMeetingModal" @update:open="meetingStore.showNewMeetingModal = $event">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Planifier un rendez-vous</DialogTitle>
      </DialogHeader>

      <div class="space-y-4 py-4">
        <div class="space-y-2">
          <Label for="patient">Patient</Label>
          <Select v-model="newMeeting.patientId">
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner un patient" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="patient in meetingStore.patients"
                :key="patient.id"
                :value="patient.id"
              >
                {{ patient.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div class="space-y-2">
          <Label for="title">Titre</Label>
          <Input
            id="title"
            v-model="newMeeting.title"
            placeholder="Titre du rendez-vous"
          />
        </div>

        <div class="space-y-2">
          <Label for="description">Description (optionnelle)</Label>
          <Textarea
            id="description"
            v-model="newMeeting.description"
            placeholder="Description du rendez-vous"
            rows="3"
          />
        </div>

        <div class="space-y-2">
          <Label for="startDate">Date et heure</Label>
          <DateTimePicker
            v-model="newMeeting.startTime"
            placeholder="Sélectionner date et heure du rendez-vous"
          />
        </div>

        <div class="space-y-2">
          <Label for="duration">Durée (minutes)</Label>
          <Select v-model="newMeeting.duration">
            <SelectTrigger>
              <SelectValue placeholder="Durée du rendez-vous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="15">
                15 minutes
              </SelectItem>
              <SelectItem :value="30">
                30 minutes
              </SelectItem>
              <SelectItem :value="45">
                45 minutes
              </SelectItem>
              <SelectItem :value="60">
                1 heure
              </SelectItem>
              <SelectItem :value="90">
                1 heure 30 minutes
              </SelectItem>
              <SelectItem :value="120">
                2 heures
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div v-if="schedulingError" class="text-red-500 text-sm mt-2">
          {{ schedulingError }}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="meetingStore.showNewMeetingModal = false">
          Annuler
        </Button>
        <Button @click="createMeeting">
          Planifier
          <Calendar class="ml-2 h-4 w-4" />
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
